
/**
 * AddressPicker —— 收货地址选择/管理（2026-08-28 任务7 R2 去高德直连）
 *
 * 架构：本组件零厂商 API。地点搜索/逆地理编码经 services/addressSearch（后端天地图
 * Web 服务，Key 只在服务端）；GPS 坐标归一经 services/map（MapService.gpsToCanonical）。
 *
 * 降级铁律（§8）：后端未配置地图 Web 服务 → 搜索块整体收起（searchUsable=false），
 * 省市区 + 手填地址照常可保存——地址编辑绝不因地图服务不可用而卡死。
 *
 * 坐标语义：form.latitude/longitude 一律 GCJ-02（库内 canonical）；POI 搜索与 GPS
 * 产出的转换都在 service/后端边界完成，本组件不碰坐标数学。
 */
import apiConfig from '@/config/api.config'
import areaSelector from '@/mixins/areaSelector'
import { searchAddress, reverseGeocode } from '@/services/addressSearch'
import { gpsToCanonical } from '@/services/map'
import { Notify } from 'quasar'

export default {
  name: 'AddressPicker',
  mixins: [areaSelector],
  props: {
    value: { type: Boolean, default: false },
    // 'select' = 下单选地址（点项即选并关闭）；'manage' = 个人中心管理（增删改 + 设默认）
    mode: { type: String, default: 'manage' },
    selectedId: { type: String, default: '' }
  },
  data () {
    return {
      addresses: [],
      loading: false,
      editing: false,
      editingId: null, // null=新增，非空=编辑该 id
      form: { receiver_name: '', receiver_phone: '', address: '', is_default: false, latitude: null, longitude: null },
      // 地点搜索状态（不可用时 searchUsable 收起搜索块，手填照常）
      poiKeyword: '',
      poiCandidates: [],
      poiLoading: false,
      searchUsable: true,
      locating: false
    }
  },
  watch: {
    value (v) {
      if (v) {
        this.editing = false
        this.fetchAddresses()
      }
    },
    // 地址文字任意字段手改 → 立即清坐标（_picking 标志区分候选/GPS 回填，避免误清）
    selectedProvince () { this.clearCoordIfManual() },
    selectedCity () { this.clearCoordIfManual() },
    selectedDistrict () { this.clearCoordIfManual() },
    'form.address' () { this.clearCoordIfManual() }
  },
  created () {
    // 实例级非响应式状态：防抖 timer / 竞态序号 / 回填标志
    this._poiTimer = null
    this._poiSeq = 0
    this._picking = false
  },
  methods: {
    async fetchAddresses () {
      this.loading = true
      try {
        const res = await this.axios.get(apiConfig.apiList.addressList)
        if (res.data.code === 200) this.addresses = res.data.data || []
      } catch (e) {
        console.error('获取地址列表失败:', e)
      } finally {
        this.loading = false
      }
    },
    pick (addr) {
      this.$emit('select', addr)
      this.$emit('input', false)
    },
    startAdd () {
      this.editingId = null
      this.form = { receiver_name: '', receiver_phone: '', address: '', is_default: this.addresses.length === 0, latitude: null, longitude: null }
      this.selectedProvince = '广西壮族自治区'
      this.selectedCity = '贺州市'
      this.selectedDistrict = '八步区'
      this.poiKeyword = ''
      this.poiCandidates = []
      this.editing = true
    },
    startEdit (addr) {
      this.editingId = addr.id
      this.form = {
        receiver_name: addr.receiver_name,
        receiver_phone: addr.receiver_phone,
        address: addr.address,
        is_default: !!addr.is_default,
        latitude: addr.latitude != null && addr.latitude !== '' ? Number(addr.latitude) : null,
        longitude: addr.longitude != null && addr.longitude !== '' ? Number(addr.longitude) : null
      }
      // 回填省市区会触发 watch → _picking 暂时屏蔽清坐标
      this._picking = true
      this.selectedProvince = addr.province || '广西壮族自治区'
      this.selectedCity = addr.city || '贺州市'
      this.$nextTick(() => { this.selectedDistrict = addr.district || '八步区' })
      this.$nextTick(() => { this.$nextTick(() => { this._picking = false }) })
      this.poiKeyword = ''
      this.poiCandidates = []
      this.editing = true
    },
    clearCoordIfManual () {
      if (this._picking) return
      if (this.form.latitude != null || this.form.longitude != null) {
        this.form.latitude = null
        this.form.longitude = null
      }
    },
    async saveForm () {
      if (!this.form.receiver_name || !this.form.receiver_phone || !this.form.address) {
        Notify.create({ message: '请填写完整信息', color: 'negative', position: 'top' }); return
      }
      if (!/^1[3-9]\d{9}$/.test(this.form.receiver_phone)) {
        Notify.create({ message: '请填写正确的手机号', color: 'negative', position: 'top' }); return
      }
      const payload = {
        receiver_name: this.form.receiver_name,
        receiver_phone: this.form.receiver_phone,
        province: this.selectedProvince,
        city: this.selectedCity,
        district: this.selectedDistrict,
        address: this.form.address,
        is_default: this.form.is_default,
        latitude: this.form.latitude,
        longitude: this.form.longitude
      }
      try {
        const base = apiConfig.apiList.addressList
        if (this.editingId) {
          await this.axios.put(`${base}/${this.editingId}`, payload)
        } else {
          await this.axios.post(base, payload)
        }
        Notify.create({ message: '保存成功', color: 'positive', position: 'top' })
        this.editing = false
        this.fetchAddresses()
        this.$emit('changed')
      } catch (e) {
        Notify.create({ message: (e.response && e.response.data && e.response.data.message) || '保存失败', color: 'negative', position: 'top' })
      }
    },
    setDefault (addr) {
      this.$q.dialog({
        title: '设为默认',
        message: `将「${addr.receiver_name}」的地址设为默认？`,
        ok: '确定',
        cancel: '取消'
      }).onOk(async () => {
        try {
          await this.axios.put(`${apiConfig.apiList.addressList}/${addr.id}/default`)
          this.fetchAddresses()
          this.$emit('changed')
        } catch (e) { console.error(e) }
      })
    },
    remove (addr) {
      this.$q.dialog({
        title: '删除地址',
        message: `确定删除「${addr.receiver_name}」的地址？`,
        ok: { label: '删除', color: 'negative', flat: true },
        cancel: '取消'
      }).onOk(async () => {
        try {
          await this.axios.delete(`${apiConfig.apiList.addressList}/${addr.id}`)
          Notify.create({ message: '已删除', color: 'positive', position: 'top' })
          this.fetchAddresses()
          this.$emit('changed')
        } catch (e) { console.error(e) }
      })
    },

    // ========== 地点搜索 / GPS 定位（零厂商 API；services 层承接） ==========
    // 搜索范围 region：优先已选城市（后端 specify 行政区参数），未选则全国
    poiRegion () {
      return this.selectedCity || ''
    },
    onPoiInput () {
      clearTimeout(this._poiTimer)
      const kw = this.poiKeyword
      this._poiTimer = setTimeout(() => this.doSearchPoi(kw), 400) // debounce 400ms
    },
    async doSearchPoi (keyword) {
      const kw = String(keyword || '').trim()
      if (kw.length < 2) { this.poiCandidates = []; this.poiLoading = false; return }
      const seq = ++this._poiSeq // 竞态防护：只认最新序号的响应，旧响应丢弃
      this.poiLoading = true
      const r = await searchAddress(kw, this.poiRegion())
      if (seq !== this._poiSeq) return // 旧响应覆盖防护
      this.poiLoading = false
      if (r.unavailable) {
        this.searchUsable = false // 后端未配置 → 收起搜索块，手填照常（不再反复探测）
        return
      }
      this.poiCandidates = r.success ? r.results.slice(0, 8) : []
    },
    // 选中候选 → 后端已给 GCJ-02 坐标，直接回填（不再有补搜/兜底链）
    pickPoiCandidate (poi) {
      this._picking = true
      if (poi.province) this.selectedProvince = poi.province
      if (poi.city) this.selectedCity = poi.city
      // 天地图 POI 无独立 district 字段：从名称+地址尽力拆区县，拆不出保留当前值
      const combined = `${poi.city}${poi.address}${poi.name}`
      const mDist = combined.match(/市([^市]+?(?:区|县|旗|市))/)
      if (mDist) this.$nextTick(() => { this.selectedDistrict = mDist[1] })
      // 详细地址：POI 名称（地址主体）；坐标与显示地址绑定
      this.form.address = poi.name || poi.address || ''
      this.form.latitude = poi.latitude
      this.form.longitude = poi.longitude
      this.poiCandidates = []
      this.poiKeyword = ''
      this.$nextTick(() => { this.$nextTick(() => { this._picking = false }) })
    },
    // 「使用当前位置」：GPS(WGS84) → MapService.gpsToCanonical → GCJ-02 → 逆地理回填（地址可再编辑）
    async useCurrentLocation () {
      if (!navigator || !navigator.geolocation) { Notify.create({ message: '设备不支持定位', color: 'negative', position: 'top' }); return }
      if (this.locating) return
      this.locating = true
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          // WGS84 → GCJ-02（严禁直接存原始 GPS 坐标；转换在 service 层）
          const c = gpsToCanonical(pos.coords.latitude, pos.coords.longitude)
          const r = await reverseGeocode(c.lat, c.lng)
          this.locating = false
          if (r.unavailable) {
            this.searchUsable = false
            Notify.create({ message: '定位服务不可用，请手填或搜索', color: 'warning', position: 'top' })
            return
          }
          this._picking = true
          if (r.success && r.formatted_address) {
            this.applyReverseAddress(r.formatted_address)
          }
          // 逆地理失败也保留坐标（地址留空让用户手填）；坐标与地址文字绑定由 watch 保证
          this.form.latitude = c.lat
          this.form.longitude = c.lng
          this.$nextTick(() => { this.$nextTick(() => { this._picking = false }) })
        },
        (err) => {
          this.locating = false
          // GPS 拒绝/失败不影响手填/搜索
          Notify.create({ message: '无法获取定位：' + (err.message || '已拒绝'), color: 'warning', position: 'top' })
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
      )
    },
    // 逆地理地址文本 → 省/市/区尽力拆分回填（拆不出保留当前值），详细地址去掉省市区前缀
    applyReverseAddress (full) {
      const mProv = full.match(/^([^市]+?(?:省|自治区|北京市|天津市|上海市|重庆市))/)
      const mCity = full.match(/([^市]+?市)/)
      const mDist = full.match(/市([^市]+?(?:区|县|旗|市))/)
      const prov = mProv ? mProv[1] : ''
      const city = mCity ? mCity[1] : ''
      const dist = mDist ? mDist[1] : ''
      if (prov) this.selectedProvince = prov
      if (city) this.selectedCity = city
      if (dist) this.$nextTick(() => { this.selectedDistrict = dist })
      this.form.address = full.replace(prov, '').replace(city, '').replace(dist, '').trim()
    }
  }
}
